import React, { useState, useEffect, useRef } from 'react';
import styles from './member.module.css';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { AddUser, Admin, Info, UpdateUser, User } from '../../types';
import { useNotification } from '../notification';
import { useFetchUsers } from '../../actions';
import { OrganizationService } from '../../services';
import { PhoneNumberInput } from '../phone-input';
import { CustomCalendar } from '../custom-calendar';
import { Button } from '../button';
import moment from 'moment';
import { ManualDate } from '../manual-date/manual-date';
import { Currency } from '../../constants';
import { CurrencyByCountryCode } from '../../constants';

type AddNewMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
  client?: User;
  admin?: Admin | null;
};

export const MemberModal: React.FC<AddNewMemberModalProps> = ({
  isOpen,
  onClose,
  client,
  admin,
}) => {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const { plans } = useSelector((state: RootState) => state.organization);
  const { rates } = useSelector((state: RootState) => state.rates);

  const [formData, setFormData] = useState<any>({
    phone: { number: '', countryCode: '' },
    startDate: null,
    endDate: null,
    plan: null,
    isEnabled: true,
  });

  const [formErrors, setFormErrors] = useState({
    phone: '',
    startDate: '',
    endDate: '',
    plan: '',
  });

  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [initialValue, setInitialValue] = useState('');
  const [initialEquivalentValue, setInitialEquivalentValue] = useState('');

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const startCalendarRef = useRef<HTMLDivElement | null>(null);
  const endCalendarRef = useRef<HTMLDivElement | null>(null);
  const fetchUsers = useFetchUsers();

  const [manualEndDate, setManualEndDate] = useState<boolean>(true);
  const [durationMonths, setDurationMonths] = useState<number>(1);

  const handleDurationChange = (months: number) => {
    const newDuration = Math.min(99, Math.max(0, months));
    setDurationMonths(newDuration);
    calculateEndDate(newDuration);
  };

  const handleToggleManualEndDate = () => {
    setManualEndDate(false);
  };

  const handleDelete = async () => {
    if (client) {
      const { error } = await OrganizationService.removeUser(client.userId);
      if (!error) {
        addNotification({
          title: t('clientDeleted'),
          message: `${client.user.fullName || client.user.phone || '-'} ${t('deletedMessage')}`,
          type: 'success',
        });
        localStorage.removeItem('search');
        localStorage.removeItem('selectedColumn');
        localStorage.removeItem('sortDirection');
        fetchUsers();
        setShowDeleteConfirmation(false);
        handleClose();
      }
    }
  };

  useEffect(() => {
    setFormData({
      phone: { number: '', countryCode: '' },
      startDate: null,
      endDate: null,
      plan: null,
      isEnabled: true,
    });
    setFormErrors({
      phone: '',
      startDate: '',
      endDate: '',
      plan: '',
    });
  }, []);

  useEffect(() => {
    if (client) {
      const {
        user: { phone },
        startAt,
        endAt,
        planId,
        isEnabled,
      } = client;
      setFormData({
        phone: { number: phone },
        startDate: startAt,
        endDate: endAt,
        plan: planId,
        isEnabled: isEnabled,
      });
      setManualEndDate(true);
    }
  }, [client]);

  useEffect(() => {
    if (client && formData.plan) {
      setDurationMonths(moment(client.endAt).diff(moment(client.startAt), 'month'));
      setInitialValue(amount());
      setInitialEquivalentValue(equivalentAmount());
      const isExactDay = moment(client.startAt).date() === moment(client.endAt).date();
      if (!isExactDay) {
        setManualEndDate(false);
      }
    }
  }, [client, formData.plan]);

  useEffect(() => {
    if (!client) {
      if (formData.startDate) {
        if (manualEndDate) {
          handleDurationChange(1);
        } else {
          handleInputChange('endDate', moment(formData.startDate).add(1, 'day'));
        }
      } else {
        handleInputChange('startDate', moment());
      }
    }
  }, [formData.startDate, manualEndDate, client]);

  useEffect(() => {
    if (showStartCalendar || showEndCalendar) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showStartCalendar, showEndCalendar]);

  const calculateEndDate = (months: number) => {
    if (formData.startDate) {
      const endDate = moment(formData.startDate).add(months, 'months').toISOString();
      handleInputChange('endDate', endDate);
    }
  };
  const validateForm = () => {
    const {
      phone: { number, countryCode },
      startDate,
      endDate,
      plan,
    } = formData;
    const errors = {
      phone: number?.trim() ? '' : 'Phone number is required.',
      startDate: startDate ? '' : 'Start date is required.',
      endDate: endDate ? '' : 'End date is required.',
      plan: plan ? '' : 'Plan is required.',
    };

    setFormErrors(errors);

    const hasErrors = Object.values(errors).some((err) => err !== '');

    const isPhoneValid =
      !client && number && countryCode && validatePhoneNumber(number, countryCode);

    if (hasErrors || (!client && !isPhoneValid)) {
      const notificationMessages = [''];
      if (hasErrors) {
        notificationMessages.push(t('mandatoryFieldsError'));
      }
      if (!isPhoneValid) {
        notificationMessages.push(t('invalidPhoneNumber'));
      }

      addNotification({
        title: t('error'),
        message: notificationMessages,
        type: 'error',
      });
    }

    return !hasErrors && (client || isPhoneValid);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleInputPhoneNumber = (number: string, countryCode: string) => {
    setFormData((prev: any) => ({ ...prev, phone: { number, countryCode } }));
    setFormErrors((prev) => ({ ...prev, phone: '' }));
  };

  const validatePhoneNumber = (number: string, countryCode: any) => {
    const phoneNumber = parsePhoneNumberWithError(number, countryCode.toUpperCase());
    return phoneNumber.isValid();
  };

  const handleOutsideClick = (e: MouseEvent) => {
    const { current: currentStart } = startCalendarRef;
    const { current: currentEnd } = endCalendarRef;
    if (currentStart && !currentStart.contains(e.target as Node)) {
      setShowStartCalendar(false);
    }

    if (currentEnd && !currentEnd.contains(e.target as Node)) {
      setShowEndCalendar(false);
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      const { user = {}, phone = {}, startDate, endDate, plan } = formData;
      const { phone: { number: userPhoneNumber = '' } = {} } = user;
      const { number: formPhoneNumber } = phone || {};

      const dto: AddUser = {
        phone: userPhoneNumber || formPhoneNumber,
        startAt: new Date(startDate),
        endAt: new Date(endDate),
        planId: Number(plan),
      };

      const { error } = await OrganizationService.addUser(dto);
      if (!error) {
        localStorage.removeItem('search');
        localStorage.removeItem('selectedColumn');
        localStorage.removeItem('sortDirection');
        fetchUsers();
        addNotification({
          title: t('newClient'),
          message: t('newClientAdded'),
          type: 'success',
        });
        return handleClose();
      }
    }
  };

  const handleUpdate = async () => {
    if (validateForm() && client) {
      const { endDate } = formData;
      if (moment(endDate).isSameOrBefore(moment(client.endAt), 'day')) {
        return addNotification({
          title: t('error'),
          message: t('endDateError'),
          type: 'error',
        });
      }
      const { id, user: { info = {} as Info, phone = undefined } = {} } = client || {};

      const dto: UpdateUser = {
        organisationUserId: id,
        endAt: new Date(endDate),
      };

      const { error } = await OrganizationService.updateUser(dto);
      if (!error) {
        localStorage.removeItem('search');
        localStorage.removeItem('selectedColumn');
        localStorage.removeItem('sortDirection');
        fetchUsers();
        addNotification({
          title: t('updateClient'),
          message: `${
            [info?.firstName, info?.midName, info?.lastName].filter(Boolean).join(' ') ||
            phone ||
            '-'
          } ${t('editedSuccessfully')}`,
          type: 'success',
        });
        return handleClose();
      }
    }
  };

  const handleClose = () => {
    if (!client) {
      setFormData({
        phone: { number: '', countryCode: '' },
        startDate: null,
        endDate: null,
        plan: null,
        isEnabled: true,
      });
      setFormErrors({
        phone: '',
        startDate: '',
        endDate: '',
        plan: '',
      });
      setDurationMonths(1);
      setManualEndDate(true);
      setInitialValue('');
      setInitialEquivalentValue('');
    }
    return onClose();
  };

  const amount = (edit?: boolean) => {
    if (!formData.plan || !formData.endDate) return '0 MDL';

    const plan = plans?.find((i) => Number(formData.plan) === i.plan.id)?.plan;
    if (!plan) return '0 MDL';

    let startDate = moment(edit ? client?.endAt : formData.startDate);
    const endDate = moment(formData.endDate);

    if (
      edit &&
      startDate.date() !== moment(formData.startDate).date() &&
      moment(startDate).diff(moment(formData.startDate), 'months') < 1 &&
      startDate.date() < moment(formData.startDate).date()
    ) {
      startDate.date(moment(formData.startDate).date());
    }

    const months = endDate.diff(startDate, 'months', true);

    const daysAfterMonth = endDate.diff(
      startDate.clone().add(Math.floor(months), 'months'),
      'days',
    );

    const isExactMonth = daysAfterMonth === 0;
    const totalAmount = isExactMonth
      ? Math.floor(months) * (plan.price / 100)
      : Math.floor(months) * (plan.price / 100) + daysAfterMonth * (plan.price / 100 / 30.5);

    return `${formatCurrency(totalAmount)} ${plan.currency}`;
  };

  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('de-DE', {
      currency: currency || undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const equivalentAmount = (edit?: boolean) => {
    if (!formData.plan || !formData.endDate || !admin) return '0 MDL';

    const plan = plans?.find((i) => +formData.plan === i.plan.id)?.plan;
    const currencyByCountryCode = CurrencyByCountryCode[admin.phoneCountryCode];
    if (!plan) return `0 ${currencyByCountryCode}`;

    if (plan.currency === Currency.MDL || plan.currency === Currency.KZT) return '';

    const startDate = moment(edit ? client?.endAt : formData.startDate);
    const endDate = moment(formData.endDate);

    if (
      edit &&
      startDate.date() !== moment(formData.startDate).date() &&
      moment(startDate).diff(moment(formData.startDate), 'months') < 1 &&
      startDate.date() < moment(formData.startDate).date()
    ) {
      startDate.date(moment(formData.startDate).date());
    }

    const months = endDate.diff(startDate, 'months', true);

    const daysAfterMonth = endDate.diff(
      startDate.clone().add(Math.floor(months), 'months'),
      'days',
    );

    const isExactMonth = daysAfterMonth === 0;
    const totalAmount = isExactMonth
      ? Math.floor(months) * (plan.price / 100)
      : Math.floor(months) * (plan.price / 100) + daysAfterMonth * (plan.price / 100 / 30.5);
    const exchangeRate = rates[plan.currency];
    if (!exchangeRate) return `0 ${currencyByCountryCode}`;
    return `${formatCurrency(totalAmount / exchangeRate)} ${currencyByCountryCode}`;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {client ? t('editClientInOrganisation') : t('addClientInOrganisation')}
          </h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
          >
            &times;
          </button>
        </div>
        <form className={styles.modalForm}>
          <div className={client ? styles.disabled : ''}>
            <label>
              {t('phoneNr')}.*
              <PhoneNumberInput
                value={formData.phone.number ? formData.phone.number : ''}
                onChange={(value: string, data: any) => {
                  handleInputPhoneNumber(value, data.countryCode);
                }}
                error={formErrors.phone}
                disabled={!!client}
                country={
                  client
                    ? parsePhoneNumberWithError(`+${client.user.phone}`).country
                    : admin?.phoneCountryCode
                }
              />
            </label>
          </div>

          <div>
            <label className={styles.noMarginTop}>
              {t('plan')}*
              <select
                value={formData.plan || ''}
                onChange={(e) => handleInputChange('plan', e.target.value)}
                className={`${styles.inputField} ${formErrors.plan ? styles.errorField : ''} ${client ? styles.disabled : ''}`}
                disabled={!!client}
              >
                <option value="">{t('selectPlan')}</option>
                {plans &&
                  plans.length > 0 &&
                  plans.map((i) => (
                    <option
                      key={i.id}
                      value={i.plan.id}
                    >
                      {`${t('plan')} ${i.plan.planType} ${i.plan.insuranceType} - ${+i.plan.price / 100} ${i.plan.currency}` +
                        (i.plan.protectedMembersAmount !== 0
                          ? ` (${i.plan.protectedMembersAmount} ${t('members')})`
                          : '')}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          <div className={styles.dateContainer}>
            <div className={styles.dateField}>
              <label className={styles.noMarginTop}>
                {t('startedDate')}*
                <div
                  className={`${styles.dateInput} 
        ${formErrors.startDate ? styles.errorField : ''} 
        ${client ? styles.disabled : ''}`}
                  onClick={!client ? () => setShowStartCalendar(!showStartCalendar) : undefined}
                >
                  <i className="fas fa-calendar"></i>
                  <span>
                    {formData.startDate
                      ? moment(formData.startDate).format('DD/MM/YYYY')
                      : '--/--/----'}
                  </span>
                </div>
              </label>
              {!client && showStartCalendar && (
                <div
                  className={styles.calendarPopup}
                  ref={startCalendarRef}
                >
                  <CustomCalendar
                    selectedDate={formData.startDate}
                    onDateChange={(date) => {
                      handleInputChange('startDate', date);
                    }}
                    onClose={() => setShowStartCalendar(false)}
                    disabled={!!client}
                  />
                </div>
              )}
            </div>

            <div className={styles.dateField}>
              <label
                className={styles.noMarginTop}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {manualEndDate ? (
                  <div
                    className={`${!formData.startDate || (client && !client.isEnabled) ? styles.disabled : ''}`}
                  >
                    <ManualDate
                      disabled={!formData.startDate}
                      durationMonths={durationMonths}
                      onDurationChange={handleDurationChange}
                      onToggleManual={handleToggleManualEndDate}
                      endDate={formData.endDate}
                      startDate={formData.startDate}
                      user={client}
                    />
                  </div>
                ) : (
                  <>
                    {t('endDate')}*
                    <div
                      className={`${styles.dateInput} ${
                        formErrors.endDate ? styles.errorField : ''
                      } ${!formData.startDate || (client && !client.isEnabled) ? styles.disabled : ''}`}
                      onClick={() => setShowEndCalendar(!showEndCalendar)}
                    >
                      <i className="fas fa-calendar"></i>
                      <span>
                        {formData.endDate
                          ? new Date(formData.endDate).toLocaleDateString('en-GB')
                          : '--/--/----'}
                      </span>
                    </div>
                    {formData.startDate && (!client || client?.isEnabled) && (
                      <div className={styles.setMonthsContainer}>
                        <span
                          className={styles.setMonthsLink}
                          onClick={() => {
                            setShowEndCalendar(false);
                            if (!formData.endDate) {
                              const startDate = new Date(formData.startDate);
                              startDate.setMonth(startDate.getMonth() + 1);
                              handleInputChange('endDate', startDate.toISOString());
                              return setManualEndDate(true);
                            }
                            const diff = moment(formData.endDate).diff(
                              moment(formData.startDate),
                              'month',
                            );
                            handleDurationChange(diff === 0 ? 1 : diff);
                            setManualEndDate(true);
                          }}
                        >
                          {t('setMonths')}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </label>
              {formData.startDate && showEndCalendar && (!client || client?.isEnabled) && (
                <div
                  className={styles.calendarPopup}
                  ref={endCalendarRef}
                >
                  <CustomCalendar
                    selectedDate={formData.endDate}
                    onDateChange={(date) => handleInputChange('endDate', date)}
                    onClose={() => setShowEndCalendar(false)}
                    disabled={!formData.startDate}
                    minDate={client ? client.endAt : formData.startDate}
                  />
                </div>
              )}
            </div>
          </div>

          {client &&
            client.isEnabled &&
            (moment(formData.endDate).diff(client.startAt, 'day') >
              moment(client.endAt).diff(client.startAt, 'day') ||
              durationMonths > moment(client.endAt).diff(client.startAt, 'month')) && (
              <label className={styles.noMarginTop}>
                <div className={styles.amountContainer}>
                  <div className={styles.amountValueWrapper}>
                    <span
                      id={styles.amountValue}
                      className={styles.amountValue}
                    >
                      {initialValue}
                    </span>
                    <span className={styles.equivalentAmount}>{initialEquivalentValue}</span>
                  </div>
                  <div className={styles.amountValueWrapper}>
                    <span
                      id={styles.amountValueGreen}
                      className={styles.amountValue}
                    >
                      +{amount(true)}
                    </span>
                    <span
                      id={styles.equivalentAmountGreen}
                      className={styles.equivalentAmount}
                    >
                      +{equivalentAmount(true)}
                    </span>
                  </div>
                </div>
              </label>
            )}

          {formData.plan && formData.endDate && (!client || client.isEnabled) && (
            <label className={styles.noMargin}>
              <div className={styles.amountContainer}>
                <div className={styles.amountLabel}>{t('amount')}:</div>
                <div className={styles.amountValueWrapper}>
                  <span className={styles.amountValue}>{amount()}</span>
                  <span className={styles.equivalentAmount}>{equivalentAmount()}</span>
                </div>
              </div>
            </label>
          )}

          <p className={styles.note}>{t('mandatoryField')}</p>
          <div className={styles.modalActions}>
            {client && !client.isEnabled ? (
              <>
                <Button
                  id={styles.deleteButton}
                  className={styles.deleteButton}
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  {t('deleteClient')}
                </Button>
              </>
            ) : client?.isEnabled ? (
              <>
                <Button
                  className={styles.deleteButton}
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  {t('deleteClient')}
                </Button>
                <Button
                  className={styles.cancelButton}
                  onClick={handleClose}
                >
                  {t('cancel')}
                </Button>
                <Button onClick={() => handleUpdate()}>{t('save')}</Button>
              </>
            ) : (
              <>
                <Button
                  className={styles.cancelButton}
                  onClick={handleClose}
                >
                  {t('cancel')}
                </Button>
                <Button onClick={() => handleSubmit()}>{t('save')}</Button>
              </>
            )}
          </div>
          {showDeleteConfirmation && (
            <div className={styles.confirmationOverlay}>
              <div className={styles.confirmationModal}>
                <p>{t('wantToDelete')}</p>
                <div className={styles.confirmationActions}>
                  <Button
                    className={styles.yesButton}
                    onClick={handleDelete}
                  >
                    {t('yes')}
                  </Button>
                  <Button
                    className={styles.noButton}
                    onClick={() => setShowDeleteConfirmation(false)}
                  >
                    {t('no')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
